// TL-7 clipboard + context-menu edit operations (UI-4), all as pure command builders.
import { type Vec } from "../core/geometry";
import { closestOnSeg, distPointSeg } from "../core/intersect";
import { simplifyRDP } from "../core/simplify";
import { newId } from "../doc/document";
import type { Doc, Entity, Style } from "../doc/entities";
import type { Command, ZRef } from "../doc/commands";
import { shiftPivotInPlace } from "./pivot";
import { remapPipeForPaste, shiftPipeInPlace } from "../pipes/carry";

export interface ClipboardData {
  entities: Entity[]; // cloned snapshots, group children included, original ids kept for remapping
}

/** Collect `ids` plus group descendants as cloned snapshots. */
export function copyEntities(doc: Doc, ids: Iterable<string>): ClipboardData {
  const out: Entity[] = [];
  const seen = new Set<string>();
  const visit = (id: string): void => {
    if (seen.has(id)) return;
    const entity = doc.entities[id];
    if (!entity) return;
    seen.add(id);
    out.push(structuredClone(entity));
    if (entity.type === "group") entity.children.forEach(visit);
  };
  for (const id of ids) visit(id);
  return { entities: out };
}

export function translateInPlace(entity: Entity, d: Vec): void {
  const mv = (p: Vec): Vec => ({ x: p.x + d.x, y: p.y + d.y });
  shiftPivotInPlace(entity, d);
  shiftPipeInPlace(entity, d); // fitting ports, free pipe ends
  switch (entity.type) {
    case "polyline":
    case "marker":
      entity.points = entity.points.map(mv);
      break;
    case "circle":
    case "arc":
      entity.center = mv(entity.center);
      break;
    case "text":
    case "image":
    case "grid":
      entity.pos = mv(entity.pos);
      break;
    case "label":
      entity.head = mv(entity.head);
      entity.tail = mv(entity.tail);
      break;
    case "dimension":
      for (const anchor of [entity.a, entity.b]) {
        if (anchor.kind === "free") anchor.p = mv(anchor.p);
      }
      break;
    case "group":
      break;
  }
}

/**
 * TL-7 paste/duplicate: clones with fresh ids at +offset. Pasted entities land on
 * `targetLayerId` (see DECISIONS.md), or keep their source layer when it is null
 * (Ctrl-drag copy / Ctrl+D). Groups are re-created via `group` commands so children
 * leave zOrder atomically (INV-1).
 */
export function pasteCommand(
  clip: ClipboardData,
  targetLayerId: string | null,
  offset: Vec
): { cmd: Command; newIds: string[] } | null {
  if (clip.entities.length === 0) return null; // EC-TL-11
  const idMap = new Map(clip.entities.map((e) => [e.id, newId()]));
  const memberIds = new Set(clip.entities.map((e) => e.id));
  const clipById = new Map(clip.entities.map((e) => [e.id, e]));
  const clones: Entity[] = clip.entities.map((src) => {
    const clone = structuredClone(src);
    clone.id = idMap.get(src.id)!;
    clone.layerId = targetLayerId ?? clone.layerId;
    if (clone.type === "group") {
      clone.children = clone.children.filter((c) => memberIds.has(c)).map((c) => idMap.get(c)!);
    }
    // a pipe follows a pasted copy of its fitting; an end whose fitting stays behind is set free where it is
    remapPipeForPaste(src, clone, clipById, idMap);
    translateInPlace(clone, offset);
    return clone;
  });

  const groups = clones.filter((e): e is Extract<Entity, { type: "group" }> => e.type === "group");
  const nonGroups = clones.filter((e) => e.type !== "group");
  const groupChildIds = new Set(groups.flatMap((g) => g.children));

  // Order groups children-first so nested groups exist before their parent's group command.
  const ordered: typeof groups = [];
  const scheduled = new Set<string>();
  while (ordered.length < groups.length) {
    let progressed = false;
    for (const g of groups) {
      if (scheduled.has(g.id)) continue;
      const childGroupsReady = g.children.every(
        (c) => scheduled.has(c) || !groups.some((other) => other.id === c)
      );
      if (childGroupsReady) {
        ordered.push(g);
        scheduled.add(g.id);
        progressed = true;
      }
    }
    if (!progressed) throw new Error("editOps: group cycle in clipboard (INV-2 violated)");
  }

  const commands: Command[] = [];
  if (nonGroups.length > 0) {
    commands.push({
      kind: "addEntities",
      entities: nonGroups,
      z: nonGroups.map((e) => ({ layerId: e.layerId, index: -1 }))
    });
  }
  for (const g of ordered) {
    commands.push({
      kind: "group",
      group: g,
      z: { layerId: g.layerId, index: -1 },
      children: g.children.map((id) => {
        const child = clones.find((c) => c.id === id)!;
        return { id, z: { layerId: child.layerId, index: -1 } };
      })
    });
  }
  const newIds = clones.filter((e) => !groupChildIds.has(e.id)).map((e) => e.id);
  return { cmd: { kind: "batch", label: "paste", commands }, newIds };
}

/** UI-4 z-order verbs. Operates within each entity's own layer. */
export function zOrderCommand(
  doc: Doc,
  ids: string[],
  action: "front" | "forward" | "backward" | "back"
): Command | null {
  const moves: { id: string; from: ZRef; to: ZRef }[] = [];
  for (const id of ids) {
    const entity = doc.entities[id];
    if (!entity) continue;
    const list = doc.zOrder[entity.layerId] ?? [];
    const from = list.indexOf(id);
    if (from < 0) continue;
    let to: number;
    switch (action) {
      case "front":
        to = list.length - 1;
        break;
      case "forward":
        to = Math.min(from + 1, list.length - 1);
        break;
      case "backward":
        to = Math.max(from - 1, 0);
        break;
      case "back":
        to = 0;
        break;
    }
    if (to !== from) {
      moves.push({ id, from: { layerId: entity.layerId, index: from }, to: { layerId: entity.layerId, index: to } });
    }
  }
  return moves.length > 0 ? { kind: "reorder", moves } : null;
}

export function moveToLayerCommand(doc: Doc, ids: string[], targetLayerId: string): Command | null {
  const moves: { id: string; from: ZRef; to: ZRef }[] = [];
  const patches: { id: string; before: Record<string, unknown>; after: Record<string, unknown> }[] = [];
  for (const id of ids) {
    const entity = doc.entities[id];
    if (!entity || entity.layerId === targetLayerId) continue;
    const list = doc.zOrder[entity.layerId] ?? [];
    const from = list.indexOf(id);
    if (from >= 0) {
      moves.push({
        id,
        from: { layerId: entity.layerId, index: from },
        to: { layerId: targetLayerId, index: -1 }
      });
    } else {
      // group child: only its layerId field changes
      patches.push({ id, before: { layerId: entity.layerId }, after: { layerId: targetLayerId } });
    }
  }
  // 3.28.0: the parts INSIDE a moved group go to the layer too. Before, a pipe or a fitting moved to another layer kept
  // its lines on the old layer, so hiding or locking a layer did not act on the whole component.
  const patched = new Set(patches.map((p) => p.id));
  const descend = (groupId: string, depth: number): void => {
    const g = doc.entities[groupId];
    if (!g || g.type !== "group" || depth > 32) return;
    for (const c of g.children) {
      const child = doc.entities[c];
      if (!child) continue;
      if (child.layerId !== targetLayerId && !patched.has(c)) {
        patched.add(c);
        patches.push({ id: c, before: { layerId: child.layerId }, after: { layerId: targetLayerId } });
      }
      descend(c, depth + 1);
    }
  };
  for (const id of ids) if (doc.entities[id]) descend(id, 0);
  const commands: Command[] = [];
  if (moves.length > 0) commands.push({ kind: "reorder", moves });
  if (patches.length > 0) commands.push({ kind: "updateEntities", patches });
  if (commands.length === 0) return null;
  return { kind: "batch", label: "move to layer", commands };
}

export function setLockedCommand(doc: Doc, ids: string[], locked: boolean): Command | null {
  const patches = ids
    .filter((id) => doc.entities[id] && doc.entities[id]!.locked !== locked)
    .map((id) => ({ id, before: { locked: !locked }, after: { locked } }));
  return patches.length > 0 ? { kind: "updateEntities", patches } : null;
}

export function applyStyleCommand(doc: Doc, ids: string[], style: Style): Command | null {
  const patches: { id: string; before: Record<string, unknown>; after: Record<string, unknown> }[] = [];
  const visit = (id: string): void => {
    const entity = doc.entities[id];
    if (!entity) return;
    if (entity.type === "group") {
      entity.children.forEach(visit);
      return;
    }
    if ("style" in entity) {
      patches.push({
        id,
        before: { style: structuredClone(entity.style) },
        after: { style: structuredClone(style) }
      });
    }
  };
  ids.forEach(visit);
  return patches.length > 0 ? { kind: "updateEntities", patches } : null;
}

// ---- component colour (3.18.0) --------------------------------------------------
// Owner, 2026-10-21: "I need to be able to set the colour of components; as it stands I can not set the colour of a
// bend, a pump or even a pipe." A pipe, a fitting, a pump or a lint pot is a GROUP of lines, and only a single line
// had a colour control. The colour goes on every line of the group (and of the groups inside it); the width, the line
// kind (solid, dashed, centre line) and the fill stay. The text of a component (a pump label) has its own colour.
// The redraws keep it: a pipe and a fitting change points only, a pump and a lint pot take the look of their old parts.

/** The lines and the texts under `ids`, group children included. Locked entities are left out. */
function colourTargets(doc: Doc, ids: string[]): { lines: Entity[]; texts: Entity[] } {
  const lines: Entity[] = [];
  const texts: Entity[] = [];
  const seen = new Set<string>();
  const visit = (id: string): void => {
    if (seen.has(id)) return;
    seen.add(id);
    const e = doc.entities[id];
    if (!e || e.locked) return;
    if (e.type === "group") e.children.forEach(visit);
    if ("style" in e) lines.push(e);
    if (e.type === "text" || e.type === "label") texts.push(e);
  };
  ids.forEach(visit);
  return { lines, texts };
}

/** The colour most of the lines (and of the texts) have now; null when there are none. */
export function componentColours(doc: Doc, ids: string[]): { line: string | null; text: string | null; lines: number; texts: number } {
  const { lines, texts } = colourTargets(doc, ids);
  const most = (cs: string[]): string | null => {
    const n = new Map<string, number>();
    for (const c of cs) n.set(c, (n.get(c) ?? 0) + 1);
    let best: string | null = null;
    for (const [c, k] of n) if (best === null || k > n.get(best)!) best = c;
    return best;
  };
  return {
    line: most(lines.map((e) => (e as Entity & { style: Style }).style.strokeColor.toLowerCase())),
    text: most(texts.map((e) => (e as Entity & { textStyle: { color: string } }).textStyle.color.toLowerCase())),
    lines: lines.length,
    texts: texts.length
  };
}

/**
 * ONE command that gives every line under `ids` the colour `line`, and every text the colour `text`. A part that has
 * the colour already is not patched. Null when nothing changes.
 */
export function componentColourCommand(doc: Doc, ids: string[], colours: { line?: string; text?: string }): Command | null {
  const { lines, texts } = colourTargets(doc, ids);
  // one patch per part: a label has a line (its arrow) and a text
  const byId = new Map<string, { id: string; before: Record<string, unknown>; after: Record<string, unknown> }>();
  const patchOf = (id: string) => byId.get(id) ?? byId.set(id, { id, before: {}, after: {} }).get(id)!;
  const line = colours.line?.toLowerCase();
  const text = colours.text?.toLowerCase();
  if (line) {
    for (const e of lines) {
      const style = (e as Entity & { style: Style }).style;
      if (style.strokeColor.toLowerCase() === line) continue;
      const p = patchOf(e.id);
      p.before.style = structuredClone(style);
      p.after.style = { ...structuredClone(style), strokeColor: line };
    }
  }
  if (text) {
    for (const e of texts) {
      const ts = (e as Entity & { textStyle: { color: string } }).textStyle;
      if (ts.color.toLowerCase() === text) continue;
      const p = patchOf(e.id);
      p.before.textStyle = structuredClone(ts);
      p.after.textStyle = { ...structuredClone(ts), color: text };
    }
  }
  const patches = [...byId.values()];
  // a batch with a label, so Undo says "colour"
  return patches.length > 0 ? { kind: "batch", label: "colour", commands: [{ kind: "updateEntities", patches }] } : null;
}

// ---- component line width (3.26.0) ----------------------------------------------
// Owner, 2026-10-23: "I need a tool to set the line width of all selected components. I am overlaying pipes and I want
// the ones on a lower layer to be thicker so they can be seen below the others that are thinner." The width goes on
// every line of the selection, group children included, as a SCREEN width (px): the width the library parts are drawn
// with (1.5 px), so it reads the same at every zoom. Inside a component the dash-dot CENTRE lines keep their width: a
// thick centre line hides the pipe. A line selected on its own takes the width whatever its kind.

/** The lines under `ids` that take a component width. Locked entities are left out. */
function widthTargets(doc: Doc, ids: string[]): Entity[] {
  const out: Entity[] = [];
  const seen = new Set<string>();
  const visit = (id: string, inGroup: boolean): void => {
    if (seen.has(id)) return;
    seen.add(id);
    const e = doc.entities[id];
    if (!e || e.locked) return;
    if (e.type === "group") e.children.forEach((c) => visit(c, true));
    if (!("style" in e)) return;
    if (inGroup && (e as Entity & { style: Style }).style.dash === "dashdot") return;
    out.push(e);
  };
  ids.forEach((id) => visit(id, false));
  return out;
}

/** The screen width (px) most of those lines have now, null when there are none or most are world widths. */
export function componentWidth(doc: Doc, ids: string[]): { px: number | null; lines: number } {
  const lines = widthTargets(doc, ids);
  const n = new Map<number, number>();
  for (const e of lines) {
    const s = (e as Entity & { style: Style }).style;
    const key = s.widthMode === "screen" ? s.strokeWidth : -1;
    n.set(key, (n.get(key) ?? 0) + 1);
  }
  let best: number | null = null;
  for (const [w, k] of n) if (best === null || k > n.get(best)!) best = w;
  return { px: best === null || best < 0 ? null : best, lines: lines.length };
}

/**
 * ONE command that gives every line under `ids` (centre lines of components excepted) the screen width `px`. The
 * colour, the line kind and the fill stay. A line that has the width already is not patched. Null when nothing changes.
 */
export function componentWidthCommand(doc: Doc, ids: string[], px: number): Command | null {
  if (!Number.isFinite(px) || px <= 0) return null;
  const patches: Array<{ id: string; before: Record<string, unknown>; after: Record<string, unknown> }> = [];
  for (const e of widthTargets(doc, ids)) {
    const style = (e as Entity & { style: Style }).style;
    if (style.widthMode === "screen" && style.strokeWidth === px) continue;
    patches.push({ id: e.id, before: { style: structuredClone(style) }, after: { style: { ...structuredClone(style), strokeWidth: px, widthMode: "screen" } } });
  }
  // a batch with a label, so Undo says "line width"
  return patches.length > 0 ? { kind: "batch", label: "line width", commands: [{ kind: "updateEntities", patches }] } : null;
}

// ---- polyline point surgery (context-menu verbs) ------------------------------

export interface VertexHit {
  entityId: string;
  index: number;
}

/** Nearest polyline vertex of `id` within tol, or null. */
export function vertexAt(doc: Doc, id: string, p: Vec, tol: number): VertexHit | null {
  const entity = doc.entities[id];
  if (!entity || entity.type !== "polyline") return null;
  let best: VertexHit & { d: number } = { entityId: id, index: -1, d: Infinity };
  entity.points.forEach((q, i) => {
    const d = Math.hypot(q.x - p.x, q.y - p.y);
    if (d <= tol && d < best.d) best = { entityId: id, index: i, d };
  });
  return best.index >= 0 ? { entityId: best.entityId, index: best.index } : null;
}

/** EC-TL-3 guard included: null when removing would violate INV-4. */
export function removeVertexCommand(doc: Doc, hit: VertexHit): Command | null {
  const entity = doc.entities[hit.entityId];
  if (!entity || entity.type !== "polyline") return null;
  const min = entity.closed ? 3 : 2;
  if (entity.points.length <= min) return null;
  const points = entity.points.filter((_, i) => i !== hit.index);
  return {
    kind: "updateEntities",
    patches: [{ id: entity.id, before: { points: entity.points }, after: { points } }]
  };
}

/** Insert a vertex on the nearest segment at the projection of `p` (within tol). */
export function addVertexCommand(doc: Doc, id: string, p: Vec, tol: number): Command | null {
  const entity = doc.entities[id];
  if (!entity || entity.type !== "polyline") return null;
  const pts = entity.points;
  const segCount = pts.length - 1 + (entity.closed && pts.length > 2 ? 1 : 0);
  let best: { i: number; q: Vec; d: number } | null = null;
  for (let i = 0; i < segCount; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const d = distPointSeg(p, a, b);
    if (d <= tol && (!best || d < best.d)) best = { i, q: closestOnSeg(p, a, b), d };
  }
  if (!best) return null;
  const points = [...pts];
  points.splice(best.i + 1, 0, best.q);
  return {
    kind: "updateEntities",
    patches: [{ id: entity.id, before: { points: pts }, after: { points } }]
  };
}

/** Context-menu "Simplify points": RDP at `tol` (world mm). Null when nothing to remove. */
export function simplifyCommand(doc: Doc, id: string, tol: number): Command | null {
  const entity = doc.entities[id];
  if (!entity || entity.type !== "polyline" || entity.points.length <= (entity.closed ? 4 : 3)) return null;
  const simplified = simplifyRDP(entity.points, tol);
  const min = entity.closed ? 3 : 2;
  if (simplified.length >= entity.points.length || simplified.length < min) return null;
  return {
    kind: "updateEntities",
    patches: [{ id: entity.id, before: { points: entity.points }, after: { points: simplified } }]
  };
}

/** Close/open a polyline (context menu). */
export function toggleClosedCommand(doc: Doc, id: string): Command | null {
  const entity = doc.entities[id];
  if (!entity || entity.type !== "polyline") return null;
  if (!entity.closed && entity.points.length < 3) return null; // INV-4
  return {
    kind: "updateEntities",
    patches: [{ id, before: { closed: entity.closed }, after: { closed: !entity.closed } }]
  };
}
